import React, { Component } from 'react';
import { Redirect } from 'react-router-dom';
import { Typography, PageHeader, Button, Modal, Alert, Divider } from 'antd';
import moment from 'moment';

import StrikeClientModel from '../../../models/StrikeClientModel';
import StrikeTeamsModel from '../../../models/StrikeTeamsModel';
import StrikeAccountTransactionsModel from '../../../models/StrikeAccountTransactionsModel';
import { isTestModeOn } from '../../../utils/testMode';
import { payoutDetailsMissing } from '../../../components/client/PayoutDetailsForm';
import FasterSigninCard from '../../../components/client/FasterSigninCard';
import { PauseButton } from '../../../components/client/AccountProfileComponents';
import ClientAvatar from '../../../components/client/ClientAvatar';
import { TeamRowList } from '../../../components/client/TeamRowList';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { CreateTeamFormMinimal } from '../../../components/client/CreateTeamFormMinimal';
import TrackerService from '../../../utils/TrackerService';
import ProfileCompletionAlert from '../../../components/client/ProfileCompletionAlert';
import BuyTippingStandsSection from '../../../components/client/BuyTippingStandsSection';

import '../../../App.scss';
import 'antd/dist/antd.css';

const { Paragraph, Text } = Typography;

const SortByPicker = ({ sortBy, setSortBy }) => {
  return (
    <Button.Group>
      <Button
        onClick={() => setSortBy('name')}
        type={sortBy === 'name' ? 'primary' : 'default'}
      >
        Sort by Name
      </Button>
      <Button
        onClick={() => setSortBy('date')}
        type={sortBy === 'date' ? 'primary' : 'default'}
      >
        Most recent
      </Button>
    </Button.Group>
  );
};


// /client/dashboard/:clientId
export default class ClientDashboardScreen extends Component {
  constructor(props) {
    super(props);

    const clientId = this.props.match.params.clientId;
    const isAdmin = !!props.isAdmin;
    this.baseURLs = props.baseURLs || {
      manage: '/client/manage',
      login: '/client/login'
    };
    this.teamsModel = new StrikeTeamsModel({viewModel: this, clientId});
    // Balances only — they decide whether the "get paid" prompt is worth showing.
    this.accountsModel = new StrikeAccountTransactionsModel({
      isTestMode: isTestModeOn(clientId),
      callback: this.updatedBalances,
      viewModel: this,
      clientId,
      excludeTransactions: true,
      onUpdateBalances: this.updatedBalances,
    });
    this.clientModel = new StrikeClientModel({
      onClientFound: this.foundClient,
      onClientNotFound: this.clientNotFound,
      viewModel: this,
      clientId,
      isAdmin,
    });


    this.state = {
      clientNotFound: false,
      route: null,
      showTestAccounts: true,
      user: null,
      client: null,
      clientId: clientId,
      percent: 0,
      teamList: [],
      loadingTeams: true,
      loadingUser: true,
      loadingClient: true,
      isAdmin,
      showAddTeamModal: false,
      sortBy: 'name',
      screenWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    };
  }

  componentDidMount() {
    this.clientModel.waitForClientData();
    if (this.accountsModel && this.accountsModel.waitForAccountsData) this.accountsModel.waitForAccountsData();
    this.handleResize = () => {
      this.setState({ screenWidth: window.innerWidth });
    };
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    this.stopLoading();
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  componentDidUpdate() {
  }

  isLoading = () => {
    return  this.state.loadingUser ||
            this.state.loadingClient ||
            this.state.loadingTeams ||
            false;
  }

  stopLoading = () => {
    if (this.teamsModel) this.teamsModel.stop();
    if (this.clientModel) this.clientModel.stop();
    if (this.accountsModel && this.accountsModel.stop) this.accountsModel.stop();
  }

  foundClient = async (client) => {

    this.setState(
      (state) => {
        return {
          clientNotFound: false,
          client,
          loadingUser: false,
          loadingClient: false,
          loadingPauseAction: false,
        }
      }
    );

    this.teamsModel.fetchAllTeamsForClient();
  }


  clientNotFound = () => {
    this.setState({
      clientNotFound: true
    });
  }

  createTeam = async (data) => {
    const isLocalHost = window.location.hostname === 'localhost';
    const isTest = isLocalHost;

    this.setState({ creatingTeam: true });

    const teamName = data.teamName;
    const teamCurrency = data.defaultCurrency;

    const newTeamId = await this.teamsModel.createTeamWithDefaultSettings({ teamName, teamCurrency });

    const tracker = new TrackerService({
      isTest,
    });

    const payloadData = {
      teamOwnerClientId: this.clientId,
      teamName,
      teamCurrency,
    }

    tracker.mixpanelTrack("create_team", payloadData);

    if (!isTest) {
      tracker.googleTrack("create_team", payloadData);
    }

    window.scrollTo(0, 0);

    this.setState({
      showAddTeamModal: false,
      newTeamId,
      newTeamName: teamName,
      creatingTeam: false,
      routeToNewTeam: true,
    });
  }


  updatedTeamsDataSource = (teams) => {
    const sortedTeamsByName = teams.sort((a, b) => {
      return a.teamName.localeCompare(b.teamName);
    });

    this.setState({
        teamList: sortedTeamsByName,
        loadingTeams: false,
    });
  }

  getProfileErrors = () => {
    const errors = [];
    const { client } = this.state;
    
    if (!client) return errors;
    
    // Check for missing required fields
    // Note: country can be null for older accounts (they're all IE anyway), so we don't require it when viewing
    if (!client.displayName) errors.push('Full name is missing');
    if (!client.phoneNumber) errors.push('Phone number is missing');
    
    // Check for invalid name format (non-company)
    if (!client.isCompany && client.displayName) {
      const words = client.displayName.trim().split(/\s+/).filter(word => word.length > 0);
      const hasLetterPattern = /[a-zA-Z]/;
      if (words.length < 2 || !words.every(word => hasLetterPattern.test(word))) {
        errors.push('We need your first and last name');
      }
    }
    
    // Check for age requirement (non-company)
    if (!client.isCompany && client.dob) {
      const dobMoment = client.dob;
      const today = moment();
      const age = today.diff(dobMoment, 'years', true);
      if (age < 13) {
        errors.push('You must be at least 13 years old');
      }
    }
    
    return errors;
  };

  // Date of birth and SSN last 4 are payout-account details (Stripe needs them
  // to pay out), not profile ones. They are asked on the Payouts screen before
  // the first bank account, and prompted here only once there is money to pay.
  getPayoutErrors = () => {
    const errors = [];
    const { client, accounts } = this.state;
    if (!client) return errors;
    const missing = payoutDetailsMissing(client, accounts);
    if (missing.dob) errors.push(client.isCompany ? 'Founded on is missing' : 'Date of birth is missing');
    if (missing.idLast4) errors.push('We need the last 4 digits of your SSN');
    if (client.idLast4 && !/^\d{4}$/.test(client.idLast4)) {
      errors.push('SSN last 4 must be exactly four digits');
    }
    return errors;
  };

  hasAnyBalance = () => (this.state.accounts || []).some((acc) => (acc && acc.available > 0));

  renderCompletionAlert = ({ title, items, footerText, page }) => {
    const base = this.baseURLs.manage || '/client/manage';
    return (
      <div style={{ paddingBottom: '16px' }}>
        <ProfileCompletionAlert
          title={title}
          items={items}
          footerText={footerText}
          variant="success"
          closable
          onClick={() => { window.location.href = `${base}/${this.state.clientId}/${page}`; }}
        />
      </div>
    );
  };

  updatedBalances = () => {
    const accounts = (this.accountsModel && this.accountsModel.accounts) || [];
    this.setState({ accounts });
  };

  render() {
    const avatar = <ClientAvatar key={`${this.state.clientId}-avatar-client`} client={this.state.client} />
    const avatarParams = { src: avatar };

    const clientNameTeams = (this.state.client && this.state.client.displayName) ? `${this.state.client.displayName}'s Teams` : null;
    const isRestricted = this.state.client && this.state.client.isRestricted;
    const isAdmin = this.props && this.props.isAdmin;
    const isMobile = this.state.screenWidth <= 768;

    const clientButtons = [
        <Button 
          key='add-team-btn' 
          onClick={() => (this.setState({ showAddTeamModal: true }))} 
          style={{ backgroundColor: '#27AE60', borderColor: '#27AE60', color: 'white',  borderRadius: '4px' }}
        >Create a team</Button>
    ];
    const adminButtons = [...clientButtons];


    if (!!this.state.routeToNewTeam && this.state.newTeamId) {
      const teamLink = isAdmin ?  `/admin/clients/${this.state.clientId}/teams/${this.state.newTeamId}` : `/client/manage/${this.state.clientId}/teams/${this.state.newTeamId}`;
      return <Redirect push to={teamLink} />
    }
    
    return (
      <div className="accountProfile" style={{ height: '100%' }}>
        { (this.state.client) && !isAdmin && 
          <div style={{ paddingBottom: '5px' }}>
            <Alert 
              message={'Create a team to get started.'} 
              type="info" 
              closable 
            />
          </div>
        }

        {(this.state.clientNotFound) &&
          (<PageHeader
            ghost={false}
            title={`Account Not Found`}
            subTitle={`Please register`}
            onBack={(() => window.history.back())}
          />)
        }
        {(this.state.client && isAdmin) &&
          (<PageHeader
            ghost={false}
            title={clientNameTeams || 'Your Teams'}
            avatar={avatarParams}
            extra={adminButtons}
            onBack={(() => window.history.back())}
          />)
        }
        {(this.state.client && !isAdmin) &&
          (<PageHeader
            ghost={false}
            title={'Your Teams'}
            extra={clientButtons}
            onBack={(() => window.history.back())}
          />)
        }
        {(this.state.client === null) &&
          (null)
        }
        {
          <Modal
            title="Create a team"
            visible={this.state.showAddTeamModal}
            onCancel={() => (this.setState({ showAddTeamModal: false, newTeamId: null, newTeamName: null }))}
            footer={[
              <Button key="back" onClick={() => (this.setState({ showAddTeamModal: false, newTeamId: null, newTeamName: null }))}>
                Cancel
              </Button>,
            ]}
          >
          <p>You can edit and customise your new team's appearance and settings later.</p>

          <CreateTeamFormMinimal
            callback={this.createTeam}
            edit={false}
            loading={this.state.creatingTeam}
            client={this.state.client}
          />
        </Modal>
        }
        {<CenteredSpinner loading={this.isLoading()} />}
        { (this.state.newTeamId) &&
          <div style={{ paddingBottom: '5px' }}>
            <Alert 
              message={`${this.state.newTeamName} successfully created. Redirecting...`} 
              type="success" 
              closable 
            />
          </div>
        }
        {!this.state.loadingTeams && this.state.teamList.length > 4 &&
          <div style={{ paddingBottom: '5px' }}>
            <SortByPicker
              sortBy={this.state.sortBy}
              setSortBy={(sortBy) => this.setState({ sortBy })}
            />
          </div>
        }
        
        {/* Get paid: only once there is a balance, and only while Stripe still lacks something. */}
        {!this.isLoading() && this.state.client && this.hasAnyBalance() && this.getPayoutErrors().length > 0
          && this.renderCompletionAlert({
            title: 'Add your details to get paid',
            items: this.getPayoutErrors(),
            footerText: 'Tap to add them and set up your bank account',
            page: 'bank',
          })}

        {/* Phone-only account: offer a free sign-in for next time, once. */}
        {!this.isLoading() && this.state.client && <FasterSigninCard />}

        {/* Complete your profile */}
        {!this.isLoading() && this.state.client && !this.state.client.isCompany && this.getProfileErrors().length > 0
          && this.renderCompletionAlert({
            title: 'Complete your profile',
            items: this.getProfileErrors(),
            footerText: 'Click anywhere to complete your profile',
            page: 'profile',
          })}

        {/* A searchable list of rows rather than a wide grid of tiles. With more
            than a handful of teams the grid became unscannable, and there was no
            way to find one by name. Same shape as the tag-claim team picker. */}
        {!this.state.loadingTeams && (() => {
          // Both orders are applied here. "Most recent" previously did nothing —
          // it fell through to whatever order the query happened to return.
          const toMillis = (value) => {
            if (!value) return 0;
            if (typeof value.toDate === 'function') return value.toDate().getTime();
            if (value instanceof Date) return value.getTime();
            return 0;
          };
          const teams = [...(this.state.teamList || [])];
          if (this.state.sortBy === 'name') {
            teams.sort((a, b) => (a.teamName || '').localeCompare(b.teamName || ''));
          } else {
            teams.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));
          }
          const base = isAdmin
            ? `/admin/clients/${this.state.clientId}/teams`
            : `/client/manage/${this.state.clientId}/teams`;
          return (
            <TeamRowList
              teams={teams}
              linkFor={(team) => `${base}/${team.teamId}`}
              emptyText="You're not on any teams yet"
            />
          );
        })()}

        {/* Buy Tipping Stands Section */}
        {!this.isLoading() && this.state.client && (
          <>
            <Divider orientation='left' />
            <BuyTippingStandsSection
              clientId={this.state.clientId}
              client={this.state.client}
              screenWidth={this.state.screenWidth}
              isAdmin={isAdmin}
              brand="salonkit"
            />
          </>
        )}
      </div>
    );
  }
}

